// services/department.service.ts

import {
  AsyncSelectFetchParams,
  AsyncSelectFetchResult,
  AutocompleteFetchFunction,
} from "@/components/form/types";
import { axiosInstance } from "@/lib/axiosInstance";

export type DepartmentStatus = "active" | "inactive";

export interface DepartmentType {
  id: string;
  code: string; // e.g. "CSE"
  name: string; // e.g. "Computer Science & Engineering"
  hod_id: string | null; // HOD ID
  hod_name: string | null; // HOD name
  established_year: number; // year, e.g. 1995
  email: string;
  phone: string;
  description: string;
  is_active: boolean;
  created_at: string;
}

export interface DepartmentCreateType {
  code: string;
  name: string;
  /** `null` clears the assigned HOD; omit the key to leave it untouched. */
  hod_id?: string | null;
  established_year: number;
  email: string;
  phone: string;
  description: string;
  status: DepartmentStatus;
}

export interface DepartmentMetaType {
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export type DepartmentParamsType = {
  search?: string;
  status?: DepartmentStatus | "all";
  page?: number;
  limit?: number;
};

export const fetchAllDepartments = async (filters: DepartmentParamsType) => {
  const params = {
    search: filters.search,
    page: filters.page,
    limit: filters.limit,
    active_only: filters.status === "active" ? true : undefined,
  };

  const res = await axiosInstance.get("/v1/departments", { params });

  // The API response seems to be a list of departments directly or wrapped in data
  const departments = res.data?.data || res.data || [];
  const total = departments.length; // If pagination is not provided in a meta object

  return {
    departments: departments,
    metaData: {
      pagination: {
        total,
        page: filters.page ?? 1,
        limit: filters.limit ?? 10,
        totalPages: Math.ceil(total / (filters.limit ?? 10)),
      },
    } as DepartmentMetaType,
  };
};

export const fetchDepartmentById = async (id: string) => {
  const res = await axiosInstance.get(`/v1/departments/${id}`);
  return res.data?.data || res.data;
};

export const createDepartment = async (data: DepartmentCreateType) => {
  const res = await axiosInstance.post("/v1/departments", data);
  return res.data?.data || res.data;
};

export const updateDepartment = async (
  id: string,
  data: DepartmentCreateType
) => {
  const res = await axiosInstance.put(`/v1/departments/${id}`, data);
  return res.data?.data || res.data;
};

export const deleteDepartment = async (id: string) => {
  const res = await axiosInstance.delete(`/v1/departments/${id}`);
  return res.data?.data || res.data;
};

export interface DepartmentDegreeCreateType {
  degree_id: string;
  name: string;
  code: string;
}

export const addDepartmentDegree = async (
  deptId: string,
  data: DepartmentDegreeCreateType
) => {
  const res = await axiosInstance.post(
    `/v1/departments/${deptId}/programs`,
    data
  );
  return res.data?.data || res.data;
};

export const updateDepartmentDegree = async (
  deptId: string,
  programId: string,
  data: DepartmentDegreeCreateType
) => {
  const res = await axiosInstance.put(
    `/v1/departments/${deptId}/programs/${programId}`,
    data
  );
  return res.data?.data || res.data;
};

export const fetchDepartmentDegrees = async (deptId: string) => {
  const res = await axiosInstance.get(`/v1/departments/${deptId}/programs`);
  return res.data?.data || res.data || [];
};

/**
 * Builds a `program_id → department code` lookup by walking every department's
 * programs. Batches reference a department only through their degree_program_id,
 * so this map lets us resolve a batch's department code.
 */
export const fetchProgramDeptCodeMap = async (): Promise<
  Record<string, string>
> => {
  const { departments } = await fetchAllDepartments({ page: 1, limit: 100 });
  const perDept = await Promise.all(
    (departments as DepartmentType[]).map(async (d) => {
      const programs = await fetchDepartmentDegrees(d.id);
      return (programs as Array<{ id: string }>).map(
        (p) => [p.id, d.code] as const
      );
    })
  );
  const map: Record<string, string> = {};
  perDept.flat().forEach(([programId, code]) => {
    map[programId] = code;
  });
  return map;
};

// Legacy paginated fetch for AsyncSingleSelectField
export const fetchDepartmentOptions = async (
  params: AsyncSelectFetchParams
): Promise<AsyncSelectFetchResult> => {
  const { search, page, pageSize } = params;
  const { departments, metaData } = await fetchAllDepartments({
    search,
    page,
    limit: pageSize,
    status: "active",
  });

  return {
    items: departments.map((d: DepartmentType) => ({
      label: d.name,
      value: d.id,
      code: d.code,
    })),
    hasMore: metaData.pagination.page < metaData.pagination.totalPages,
    total: metaData.pagination.total,
  };
};

// New simple fetch for AsyncAutocompleteSingleSelectField
export const fetchAutocompleteDepartmentOptions: AutocompleteFetchFunction =
  async ({ search, limit }) => {
    const { departments } = await fetchAllDepartments({
      search,
      page: 1,
      limit,
      status: "active",
    });

    return departments.map((d: DepartmentType) => ({
      label: d.name,
      value: d.id,
    }));
  };

export const fetchProgramById = async (id: string) => {
  const res = await axiosInstance.get(`/v1/programs/${id}`);
  return res.data?.data || res.data;
};

export const departmentService = {
  fetchAllDepartments,
  fetchDepartmentById,
  createDepartment,
  updateDepartment,
  deleteDepartment,
  fetchDepartmentOptions,
  fetchAutocompleteDepartmentOptions,
  addDepartmentDegree,
  updateDepartmentDegree,
  fetchDepartmentDegrees,
  fetchProgramDeptCodeMap,
  fetchProgramById,
};
